import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FirestoreDataService } from '../core/firestore-data.service';
import { Client, Bike, Appointment, AppointmentStatus } from '../core/models';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Observable, combineLatest, of, BehaviorSubject } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { Timestamp } from '@angular/fire/firestore';

declare var bootstrap: any;

@Component({
  selector: 'app-appointments',
  templateUrl: './appointments.component.html',
  styleUrls: ['./appointments.component.scss']
})
export class AppointmentsComponent implements OnInit {
  @ViewChild('addAppModal') addAppModal!: ElementRef;
  private bsModal: any;

  // Manejo de Fechas
  selectedDate = new Date();
  weekDays: Date[] = [];
  appointments$: Observable<Appointment[]> = of([]);
  
  // Capacidad del Taller (Configurable)
  readonly DAILY_CAPACITY = 25; 

  // Formulario y Búsqueda
  appointmentForm: FormGroup;
  clientSearch = new FormControl('');
  filteredClients$: Observable<Client[]>;
  selectedClient: Client | null = null;
  clientBikes$: Observable<Bike[]> = of([]);
  
  isSaving = false;
  isLoading = true;

  constructor(
    private firestoreService: FirestoreDataService,
    private fb: FormBuilder
  ) {
    this.selectedDate.setHours(0, 0, 0, 0);
    
    // Configuración del Formulario
    this.appointmentForm = this.fb.group({
      bikeId: ['', Validators.required],
      serviceType: ['Ride', Validators.required],
      time: ['09:00', Validators.required],
      notes: ['']
    });

    // Búsqueda reactiva de clientes
    this.filteredClients$ = combineLatest([
      this.firestoreService.getClients(),
      this.clientSearch.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([clients, term]) => {
        if (!term || typeof term !== 'string' || this.selectedClient) return [];
        const t = term.toLowerCase();
        return clients.filter(c => c.name.toLowerCase().includes(t) || c.phone?.includes(t)).slice(0, 5);
      })
    );
  }

  ngOnInit(): void {
    this.generateWeek(this.selectedDate);
    this.loadAppointments();
  }

  ngAfterViewInit() {
    this.bsModal = new bootstrap.Modal(this.addAppModal.nativeElement);
  }

  /**
   * Genera los días de la semana alrededor de la fecha seleccionada
   */
  generateWeek(baseDate: Date) {
    this.weekDays = [];
    const start = new Date(baseDate);
    start.setDate(baseDate.getDate() - 3); // 3 días antes
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      this.weekDays.push(d);
    }
  }

  loadAppointments() {
    this.isLoading = true;
    const start = new Date(this.selectedDate);
    const end = new Date(this.selectedDate);
    end.setHours(23, 59, 59, 999);
    
    this.appointments$ = this.firestoreService.getAppointmentsRange(start, end).pipe(
      map(apps => {
        this.isLoading = false;
        return apps;
      })
    );
  }

  selectDay(date: Date) {
    this.selectedDate = new Date(date);
    this.selectedDate.setHours(0,0,0,0);
    this.generateWeek(this.selectedDate);
    this.loadAppointments();
  }

  openModal() {
    this.resetForm();
    this.bsModal.show();
  }

   selectClient(client: Client) {
    this.selectedClient = client;
    this.clientSearch.setValue(client.name, { emitEvent: false });
    // Al seleccionar, el observable filteredClients$ devolverá [] gracias al check 'this.selectedClient'
    if (client.id) {
      this.clientBikes$ = this.firestoreService.getBikesForClient(client.id);
    }
  }

  clearClient() {
    this.selectedClient = null;
    this.clientSearch.setValue('');
    this.clientBikes$ = of([]);
    this.appointmentForm.patchValue({ bikeId: '' });
  }

  resetForm() {
    this.appointmentForm.reset({ serviceType: 'Ride', time: '09:00' });
    this.clearClient();
    this.isSaving = false;
  }

  async saveAppointment() {
    if (this.appointmentForm.invalid || !this.selectedClient) return;

    this.isSaving = true;
    const val = this.appointmentForm.value;
    
    // Construir fecha y hora
    const appDate = new Date(this.selectedDate);
    const [hours, mins] = val.time.split(':');
    appDate.setHours(parseInt(hours), parseInt(mins));

    // Obtener descripción de la bici
    const bikes = await this.clientBikes$.pipe(take(1)).toPromise();
    const bike = bikes?.find(b => b.id === val.bikeId);
    const bikeDesc = bike ? `${bike.brand} ${bike.model || ''} (${bike.color})` : 'Bicicleta';

    const newApp: Omit<Appointment, 'id' | 'createdAt'> = {
      clientId: this.selectedClient.id!,
      clientName: this.selectedClient.name,
      clientPhone: this.selectedClient.phone,
      bikeId: val.bikeId,
      bikeDescription: bikeDesc,
      date: Timestamp.fromDate(appDate),
      serviceType: val.serviceType,
      status: 'Scheduled',
      notes: val.notes
    };

    try {
      await this.firestoreService.addAppointment(newApp);
      this.bsModal.hide();
      alert("Cita programada con éxito.");
      this.loadAppointments();
    } catch (e) {
      alert("Error al agendar.");
    } finally {
      this.isSaving = false;
    }
  }

  async updateStatus(app: Appointment, status: AppointmentStatus) {
    try {
      await this.firestoreService.updateAppointmentStatus(app.id!, status);
      this.loadAppointments();
    } catch (e) {
      alert("Error al actualizar estado.");
    }
  }

  async deleteApp(id: string) {
    if (confirm("¿Eliminar esta cita?")) {
      await this.firestoreService.deleteAppointment(id);
      this.loadAppointments();
    }
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  }

  getCapacityClass(count: number): string {
    const pct = (count / this.DAILY_CAPACITY) * 100;
    if (pct >= 100) return 'bg-danger';
    if (pct >= 70) return 'bg-warning text-dark';
    return 'bg-success';
  }
}